import React, { useEffect, useRef, useState } from 'react';
import { 
  FaHeart, 
  FaClock, 
  FaMapMarkerAlt, 
  FaStar, 
  FaWifi, 
  FaCoffee, 
  FaParking, 
  FaShieldAlt,
  FaUsers,
  FaBed,
  FaSmile,
  FaThumbsUp,
  FaAward,
  FaGem,
  FaSpa,
  FaUtensils,
  FaArrowRight,
  FaQuoteLeft,
  FaCalendarCheck,
  FaConciergeBell,
  FaFire,
  FaSun,
  FaLeaf
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const WhyChooseUs = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredReason, setHoveredReason] = useState(null);
  const [hoveredTestimonial, setHoveredTestimonial] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const reasons = [
    {
      icon: FaGem,
      title: 'Luxury Experience',
      description: 'Experience unmatched luxury with premium amenities and elegant decor.',
      bgColor: 'from-blue-500 to-blue-600',
      hoverColor: 'text-blue-600'
    },
    {
      icon: FaUsers,
      title: 'Family Friendly',
      description: 'Perfect for family vacations with spacious rooms and kid-friendly services.',
      bgColor: 'from-orange-500 to-orange-600',
      hoverColor: 'text-orange-600'
    },
    {
      icon: FaClock,
      title: '24/7 Service',
      description: 'Round-the-clock room service and guest support for your convenience.',
      bgColor: 'from-blue-500 to-blue-600',
      hoverColor: 'text-blue-600'
    },
    {
      icon: FaMapMarkerAlt,
      title: 'Prime Location',
      description: 'Located in the heart of Varanasi with easy access to major attractions.',
      bgColor: 'from-orange-500 to-orange-600',
      hoverColor: 'text-orange-600'
    },
    {
      icon: FaSmile,
      title: 'Warm Hospitality',
      description: 'Traditional Indian hospitality with personalized care and attention.',
      bgColor: 'from-blue-500 to-blue-600',
      hoverColor: 'text-blue-600'
    },
    {
      icon: FaThumbsUp,
      title: 'Value for Money',
      description: 'Competitive pricing with luxury experience and exclusive offers.',
      bgColor: 'from-orange-500 to-orange-600',
      hoverColor: 'text-orange-600'
    },
  ];

  const testimonials = [
    {
      name: 'Rahul Sharma',
      location: 'Delhi',
      rating: 5,
      text: 'Amazing experience! The rooms were spotless and staff was very helpful. Best guest house in Varanasi.',
      image: 'https://randomuser.me/api/portraits/men/1.jpg'
    },
    {
      name: 'Priya Singh',
      location: 'Mumbai',
      rating: 5,
      text: 'Loved the hospitality! Great location, comfortable rooms, and delicious breakfast. Will visit again.',
      image: 'https://randomuser.me/api/portraits/women/1.jpg'
    },
  ];

  return (
    <section ref={sectionRef} className="py-16 px-4 bg-gradient-to-br from-white via-blue-50/30 to-orange-50/30 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        
        {/* Section Header with Animations - Blue and Orange Separate */}
        <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 bg-blue-50 backdrop-blur-sm px-4 py-1.5 rounded-full mb-3 animate-pulse-slow">
            <FaFire className="text-orange-500 text-xs animate-float" />
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Why Choose Us</span>
            <FaSun className="text-orange-500 text-xs animate-spin-slow" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">
            What Makes <span className="text-blue-600">Paridhi</span> <span className="text-orange-500">Special</span>
          </h2>
          <div className="w-24 h-1 bg-blue-500 mx-auto rounded-full animate-glow"></div>
          <div className="w-12 h-1 bg-orange-500 mx-auto rounded-full mt-1"></div>
          <p className="text-gray-600 max-w-2xl mx-auto mt-4">
            Discover why hundreds of guests choose us for their stay in Varanasi
          </p>
        </div>

        {/* Reasons Grid with Enhanced Animations */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {reasons.map((reason, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 group hover:-translate-y-2 cursor-pointer border border-gray-100 hover:border-${reason.hoverColor === 'text-blue-600' ? 'blue' : 'orange'}-200 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
              onMouseEnter={() => setHoveredReason(index)}
              onMouseLeave={() => setHoveredReason(null)}
            >
              <div className={`w-14 h-14 bg-gradient-to-r ${reason.bgColor} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-all duration-300 group-hover:rotate-6 relative overflow-hidden shadow-lg`}>
                <reason.icon className="text-white text-2xl transition-transform duration-300 group-hover:scale-110" />
                <div className="absolute inset-0 bg-white/30 rounded-xl scale-0 group-hover:scale-100 transition-transform duration-500"></div>
              </div>
              <h3 className={`text-xl font-bold text-gray-800 mb-2 group-hover:${reason.hoverColor} transition-colors duration-300`}>{reason.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed group-hover:text-gray-600 transition-colors duration-300">{reason.description}</p>
              <div className={`h-0.5 bg-gradient-to-r ${reason.bgColor} mt-3 transition-all duration-300 ${hoveredReason === index ? 'w-full' : 'w-0'}`}></div>
            </div>
          ))}
        </div>

        {/* Testimonials Section with Orange Accents */}
        <div className={`mb-16 transition-all duration-700 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-orange-50 px-4 py-1.5 rounded-full mb-3">
              <FaQuoteLeft className="text-blue-500 text-xs" />
              <span className="text-orange-600 font-semibold text-xs uppercase tracking-wider">Testimonials</span>
              <FaQuoteLeft className="text-orange-500 text-xs rotate-180" />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-800">What Our <span className="text-orange-500">Guests</span> Say</h3>
            <p className="text-gray-500 text-sm mt-2">Real experiences from real travelers</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-white to-orange-50/30 rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 cursor-pointer group border border-gray-100 hover:border-orange-200"
                onMouseEnter={() => setHoveredTestimonial(index)}
                onMouseLeave={() => setHoveredTestimonial(null)}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="relative">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.name} 
                      className="w-12 h-12 rounded-full object-cover border-2 border-blue-300 group-hover:border-orange-500 transition-all duration-300 group-hover:scale-110" 
                    />
                    <div className="absolute -bottom-1 -right-1 bg-orange-500 rounded-full p-1">
                      <FaQuoteLeft className="text-white text-[6px]" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 group-hover:text-orange-600 transition-colors duration-300">{testimonial.name}</h4>
                    <p className="text-xs text-gray-500">{testimonial.location}</p>
                    <div className="flex text-orange-400 text-xs mt-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <FaStar key={i} size={12} className="animate-twinkle" style={{ animationDelay: `${i * 0.1}s` }} />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 text-sm italic leading-relaxed relative">
                  <span className="text-blue-400 text-xl absolute -top-2 -left-1 opacity-50">"</span>
                  {testimonial.text}
                  <span className="text-orange-400 text-xl absolute -bottom-4 opacity-50">"</span>
                </p>
                <div className={`h-0.5 bg-gradient-to-r from-blue-500 to-orange-500 mt-4 transition-all duration-500 ${hoveredTestimonial === index ? 'w-full' : 'w-0'}`}></div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action - Blue Background with Orange Button */}
        <div className={`bg-blue-600 rounded-2xl p-8 text-center text-white transition-all duration-700 delay-800 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="relative">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white/10 rounded-full -translate-x-16 -translate-y-16 animate-pulse-slow"></div>
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-orange-400/20 rounded-full translate-x-20 translate-y-20 animate-float"></div>
            <div className="absolute top-1/2 left-1/2 w-24 h-24 bg-blue-400/20 rounded-full -translate-x-1/2 -translate-y-1/2 animate-spin-slow"></div>
            
            <h3 className="text-2xl md:text-3xl font-bold mb-3 relative z-10">Ready to Experience Paridhi?</h3>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto relative z-10">
              Book your stay today and enjoy exclusive offers, premium amenities, and unforgettable hospitality
            </p>
            <div className="flex flex-wrap justify-center gap-4 relative z-10">
              <Link
                to="/booking"
                className="group relative bg-orange-500 text-white px-6 py-2.5 rounded-lg font-semibold overflow-hidden transition-all shadow-md hover:shadow-xl hover:bg-orange-600"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Book Now
                  <FaCalendarCheck size={14} className="transition-transform duration-300 group-hover:scale-110" />
                </span>
              </Link>
              <Link
                to="/contact"
                className="group border-2 border-white text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-white/10 transition-all duration-300 transform hover:scale-105 flex items-center gap-2"
              >
                Contact Us
                <FaConciergeBell size={14} className="transition-all duration-300 group-hover:rotate-12" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes glow {
          0% { box-shadow: 0 0 5px rgba(59,130,246,0.3); }
          50% { box-shadow: 0 0 20px rgba(59,130,246,0.6); }
          100% { box-shadow: 0 0 5px rgba(59,130,246,0.3); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes twinkle {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.9); }
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
        
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s linear infinite;
        }
        
        .animate-glow {
          animation: glow 2s ease-in-out infinite;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
        
        .animate-twinkle {
          animation: twinkle 1.5s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default WhyChooseUs;